<div class="">
  <div class="max-content-width spaced-sections">
    <!-- Error Message Alert -->
    @if (errorMessage) {
      <app-base-alert
        [type]="'error'"
        [message]="errorMessage"
        [closeable]="true"
        (closed)="errorMessage = ''"
      ></app-base-alert>
    }

    <!-- Main Workspace -->
    <div class="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <!-- Paper / Content Area -->
      <div class="lg:col-span-3 space-y-4">
        @if (loading) {
          <div
            class="card card-padding flex flex-col items-center justify-center min-h-[400px] text-center"
          >
            <div
              class="w-10 h-10 border-4 border-secondary border-t-transparent rounded-full animate-spin"
            ></div>
            <p class="text-sm text-slate-500 mt-4">Loading next paper in queue...</p>
          </div>
        } @else if (currentPaper) {
          <!-- Action Toolbar -->
          <div class="bg-white rounded-xl border border-slate-200 shadow-sm p-2 text-sm">
            <div class="flex items-center gap-2">
              <button
                (click)="includeCurrent()"
                class="px-4 py-1.5 rounded-lg border font-semibold text-white bg-emerald-600 border-emerald-700 hover:bg-emerald-700 hover:border-emerald-800 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                [disabled]="loading"
                title="Include paper for full-text review (I)"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2.5"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Include
                <kbd class="px-1 py-0.5 text-[9px] font-bold bg-white/20 rounded">I</kbd>
              </button>

              <button
                (click)="excludeCurrent()"
                class="px-4 py-1.5 rounded-lg border font-semibold text-white bg-rose-600 border-rose-700 hover:bg-rose-700 hover:border-rose-800 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                [disabled]="loading"
                title="Exclude paper — does not meet criteria (E)"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2.5"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Exclude
                <kbd class="px-1 py-0.5 text-[9px] font-bold bg-white/20 rounded">E</kbd>
              </button>

              <button
                (click)="skipCurrent()"
                class="px-4 py-1.5 rounded-lg border border-slate-300 font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                [disabled]="loading"
                title="Skip paper — postpone to end of queue (S)"
              >
                <svg
                  class="w-4 h-4 text-slate-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2.5"
                    d="M13 5l7 7-7 7M5 5l7 7-7 7"
                  />
                </svg>
                Skip
                <kbd class="px-1 py-0.5 text-[9px] font-bold bg-slate-200 text-slate-600 rounded"
                  >S</kbd
                >
              </button>

              <button
                (click)="unlockCurrent()"
                class="px-4 py-1.5 rounded-lg border font-semibold text-orange-600 bg-white border-orange-400 hover:bg-orange-50 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                [disabled]="loading"
                title="Release paper lock and return to overview"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                  />
                </svg>
                Unlock
              </button>

              @if (paperLocked) {
                <span
                  class="text-[10px] text-slate-400"
                  title="Locks expire after 24 hours of inactivity"
                >
                  <svg
                    class="w-4 h-4 inline -mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </span>
              }
              <div class="ml-auto flex items-center gap-2">
                @if (
                  votedPapersStack().length > 0 &&
                  projectState === ProjectState.TITLE_ABSTRACT_SCREENING
                ) {
                  <button
                    (click)="undoLastVote()"
                    [disabled]="loading"
                    title="Undo last vote (U)"
                    class="px-4 py-1.5 rounded-lg border font-semibold text-amber-600 bg-white border-amber-500 hover:bg-amber-50 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                      />
                    </svg>
                    Undo
                    <kbd
                      class="px-1 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-700 rounded"
                      >U</kbd
                    >
                    <span class="text-[10px] text-amber-400 font-semibold ml-0.5"
                      >({{ votedPapersStack().length }})</span
                    >
                  </button>
                }
              </div>
            </div>
          </div>
          <!-- Paper Card -->
          <div class="card card-padding min-h-[400px] flex flex-col justify-between">
            <div>
              <div class="flex items-center gap-2 mb-4 flex-wrap">
                @if (currentPaper.venue) {
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200"
                  >
                    Venue: {{ currentPaper.venue }}
                  </span>
                }
                @if (currentPaper.sourceType) {
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200"
                  >
                    {{ currentPaper.sourceType }}
                  </span>
                }
                @if (currentPaper.workType) {
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200"
                  >
                    {{ currentPaper.workType }}
                  </span>
                }
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200"
                >
                  {{ currentPaperYear }}
                </span>
              </div>

              <h2 class="text-2xl font-extrabold text-slate-900 leading-snug mb-6">
                {{ currentPaper.title }}
              </h2>

              <div class="border-t border-slate-200/60 pt-5 mt-5">
                <h3 class="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                  Abstract
                </h3>
                <p class="text-slate-700 leading-relaxed text-left text-base whitespace-pre-line">
                  {{ currentPaper.abstractField }}
                </p>
              </div>
            </div>
          </div>
        } @else if (projectState !== ProjectState.TITLE_ABSTRACT_SCREENING) {
          <!-- Phase Complete View (stage advanced — always visible) -->
          <div class="card card-padding flex items-center justify-center min-h-[400px]">
            <div class="empty-state">
              <div class="empty-state-icon">🎉</div>
              <h3 class="empty-state-title">Title & Abstract Screening Complete!</h3>
              <p class="empty-state-message max-w-md mx-auto">
                This project has successfully completed and advanced past the Title & Abstract
                screening phase.
              </p>

              <div
                class="mt-6 flex items-center justify-center gap-6 p-4 bg-slate-50 border border-dashed border-slate-200 rounded-xl max-w-sm mx-auto"
              >
                <div class="text-center">
                  <span class="block text-2xl font-bold text-emerald-600">{{
                    stats.teamIncludedPapers ?? 0
                  }}</span>
                  <span class="text-xs text-slate-500 font-medium">Included</span>
                </div>
                <div class="w-px h-8 bg-slate-300"></div>
                <div class="text-center">
                  <span class="block text-2xl font-bold text-rose-600">{{
                    stats.teamExcludedPapers ?? 0
                  }}</span>
                  <span class="text-xs text-slate-500 font-medium">Excluded</span>
                </div>
              </div>

              <p class="text-xs text-slate-400 mt-4">
                Note: This workspace is in read-only mode because the screening stage is closed.
              </p>
            </div>
          </div>
        } @else if (!hasRequestedFirst) {
          <!-- Start Screening — prompt user to begin instead of auto-locking -->
          <div
            class="card card-padding flex flex-col items-center justify-center min-h-[400px] text-center"
          >
            <div class="empty-state-icon">
              <svg
                class="w-12 h-12 mx-auto text-secondary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-slate-800 mt-4">Start Screening</h3>
            <p class="text-sm text-slate-500 max-w-sm mx-auto mt-2">
              Pull the next paper from the queue. It will be locked to you until you vote, skip or
              unlock.
            </p>
            <button
              (click)="loadNextPaper()"
              class="mt-6 px-6 py-3 rounded-lg font-bold text-white bg-secondary hover:bg-hover transition-colors shadow-lg shadow-secondary/20 inline-flex items-center gap-2 text-base"
            >
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Start Screening
            </button>
          </div>
        } @else {
          <!-- Queue Completed View -->
          <div class="card card-padding flex items-center justify-center min-h-[400px]">
            <div class="empty-state">
              <div class="empty-state-icon">🎉</div>
              <h3 class="empty-state-title">Your Queue is Complete!</h3>
              <p class="empty-state-message max-w-md mx-auto">
                You have successfully screened all currently available papers in your queue. We are
                waiting for other team members to finish.
              </p>

              <div
                class="mt-6 flex items-center justify-center gap-6 p-4 bg-slate-50 border border-dashed border-slate-200 rounded-xl max-w-sm mx-auto"
              >
                <div class="text-center">
                  <span class="block text-2xl font-bold text-emerald-600">{{
                    stats.teamIncludedPapers ?? 0
                  }}</span>
                  <span class="text-xs text-slate-500 font-medium">Team Included</span>
                </div>
                <div class="w-px h-8 bg-slate-300"></div>
                <div class="text-center">
                  <span class="block text-2xl font-bold text-rose-600">{{
                    stats.teamExcludedPapers ?? 0
                  }}</span>
                  <span class="text-xs text-slate-500 font-medium">Team Excluded</span>
                </div>
              </div>

              @if (isPI) {
                <div
                  class="mt-8 p-6 bg-slate-50 border border-slate-200 rounded-2xl max-w-md mx-auto space-y-4"
                >
                  <h4 class="font-bold text-slate-900 text-sm">Project PI Phase Control</h4>
                  <p class="text-xs text-slate-500">
                    As the PI, you can advance this project to the Full-Text Retrieval phase once
                    all team members have finished screening.
                  </p>

                  <button
                    [disabled]="
                      stats.partiallyScreenedPapers > 0 || stats.unscreenedPapers > 0 || loading
                    "
                    (click)="advanceConfirmOpen = true"
                    class="w-full btn-primary disabled:bg-slate-200 disabled:text-slate-400 disabled:border-slate-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <i class="bi bi-arrow-right-circle-fill"></i>
                    Advance to Full-Text Retrieval
                  </button>

                  @if (stats.partiallyScreenedPapers > 0 || stats.unscreenedPapers > 0) {
                    <div
                      class="flex items-center gap-1.5 justify-center text-xs text-amber-600 font-semibold bg-amber-50 py-1.5 px-3 rounded-lg border border-amber-200"
                    >
                      <i class="bi bi-exclamation-triangle-fill"></i>
                      <span
                        >{{
                          (stats.partiallyScreenedPapers ?? 0) + (stats.unscreenedPapers ?? 0)
                        }}
                        papers are still waiting to be screened by the team.</span
                      >
                    </div>
                  } @else {
                    <div
                      class="flex items-center gap-1.5 justify-center text-xs text-green-600 font-semibold bg-green-50 py-1.5 px-3 rounded-lg border border-green-200"
                    >
                      <i class="bi bi-check-circle-fill"></i>
                      <span>All papers are fully screened! You can now advance the phase.</span>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>

      <!-- Sidebar: Progress + Agreement Stats -->
      <div class="lg:col-span-1 flex flex-col gap-4">
        <div>
          <app-screening-progress-card
            [totalPapers]="stats.totalPapers"
            [unscreenedPapers]="stats.unscreenedPapers"
            [partiallyScreenedPapers]="stats.partiallyScreenedPapers"
            [includedPapers]="stats.includedPapers"
            [excludedPapers]="stats.excludedPapers"
            [isPI]="isPI"
            [researcherProgress]="stats.researcherProgress ?? []"
            [teamTotalDecisions]="stats.teamTotalDecisions ?? 0"
            [teamTotalRequiredDecisions]="stats.teamTotalRequiredDecisions ?? 0"
          ></app-screening-progress-card>
        </div>

        <div>
          <app-agreement-stats-card
            [visible]="isPI"
            [projectId]="projectId"
            [stage]="ProjectState.TITLE_ABSTRACT_SCREENING"
            [projectState]="projectState"
          ></app-agreement-stats-card>
        </div>
      </div>
    </div>

    <!-- Screening Criteria Bar -->
    @if (project) {
      <app-screening-criteria-bar
        class="lg:col-span-4"
        [inclusionCriteria]="project?.inclusionCriteria"
        [exclusionCriteria]="project?.exclusionCriteria"
      />
    }
  </div>
</div>

<app-confirm-modal
  [(open)]="advanceConfirmOpen"
  [title]="'Advance Phase to Full-Text Retrieval'"
  [message]="'Are you sure you want to advance this project to the Full-Text Retrieval phase? This will close the Title & Abstract screening phase and lock all decisions. This action cannot be undone.'"
  (confirmed)="advanceProject()"
  (canceled)="advanceConfirmOpen = false"
></app-confirm-modal>
